import { IReturnShipments } from '@src/stores/returnStore';

/**
 * Calculates the number of shipment groups displayed in the ReviewReturnDetailsStep.
 *
 * Shipments are grouped by their return method:
 * - In-Store (PHASE_0_SORIS): Maximum 1 shipment, displayed as 1 card
 * - UPS (DROPOFF): Can have multiple shipments, each displayed as a separate card
 * - LORP (PICKUP): Can have multiple shipments, all grouped together into 1 card
 *
 * @param returnShipments - Array of return shipments
 * @returns The number of shipment groups to be displayed
 *
 * @example
 * // Returns 3 (1 In-Store + 1 UPS + 1 LORP group)
 * getDisplayedShipmentGroupCount([
 *   { shipmentType: 'PHASE_0_SORIS', ... },
 *   { shipmentType: 'DROPOFF', ... },
 *   { shipmentType: 'PICKUP', ... },
 * ])
 *
 * @example
 * // Returns 3 (1 In-Store + 2 UPS shipments, each displayed separately)
 * getDisplayedShipmentGroupCount([
 *   { shipmentType: 'PHASE_0_SORIS', ... },
 *   { shipmentType: 'DROPOFF', ... },
 *   { shipmentType: 'DROPOFF', ... },
 * ])
 *
 * @example
 * // Returns 1 (multiple LORP shipments grouped into one card)
 * getDisplayedShipmentGroupCount([
 *   { shipmentType: 'PICKUP', ... },
 *   { shipmentType: 'PICKUP', ... },
 * ])
 */
export const getDisplayedShipmentGroupCount = (
  returnShipments?: IReturnShipments[],
): number => {
  if (!returnShipments || returnShipments.length === 0) {
    return 0;
  }

  // Separate LORP and non-LORP shipments
  const lorpShipments = returnShipments.filter(
    (shipment) => shipment.shipmentType === 'PICKUP',
  );
  const nonLorpShipments = returnShipments.filter(
    (shipment) => shipment.shipmentType !== 'PICKUP',
  );

  // Count non-LORP shipments (each gets its own card)
  const nonLorpGroupCount = nonLorpShipments.length;

  // Add 1 if there are any LORP shipments (all grouped into one card)
  const lorpGroupCount = lorpShipments.length > 0 ? 1 : 0;

  return nonLorpGroupCount + lorpGroupCount;
};
